import { useEffect, useRef, useState } from "react";
import { CalendarCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/analytics";

// Signed-in-only invite to the Hero Benefits Alliance intro session. Separate from
// the general webinar pages (still turned off); reads the single "hba-intro" event.
const DISMISS_KEY = "ha_hba_webinar_dismissed_at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

interface Webinar {
  id: string;
  scheduled_at: string | null;
  video_url: string | null;
  status: string;
}

interface Props {
  placement: "dashboard" | "stack_results";
  className?: string;
}

export default function HbaWebinarInvite({ placement, className = "" }: Props) {
  const { user } = useAuth();
  const [webinar, setWebinar] = useState<Webinar | null>(null);
  const [saved, setSaved] = useState(false);
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (!user) { setVisible(false); return; }
    let cancelled = false;
    (async () => {
      const { data: w } = await supabase
        .from("webinars")
        .select("id, scheduled_at, video_url, status")
        .contains("tags", ["hba-intro"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled || !w) return;
      const { data: r } = await supabase
        .from("webinar_rsvps")
        .select("id")
        .eq("webinar_id", w.id)
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      let dismissed = false;
      try {
        const at = Number(localStorage.getItem(`${DISMISS_KEY}:${user.id}`) || 0);
        dismissed = at > 0 && Date.now() - at < DISMISS_MS;
      } catch { /* ignore */ }
      setWebinar(w as Webinar);
      setSaved(!!r);
      setVisible(!!r || !dismissed);
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (visible && !saved && !shownRef.current) {
      shownRef.current = true;
      trackEvent("hba_webinar_card_shown", { placement });
    }
  }, [visible, saved, placement]);

  if (!user || !webinar || !visible) return null;

  const when = webinar.scheduled_at ? new Date(webinar.scheduled_at) : null;
  const upcoming = when && when.getTime() > Date.now() - 2 * 60 * 60 * 1000;
  const recorded = !!webinar.video_url && (!when || !upcoming);
  const whenLabel = when
    ? when.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })
    : null;

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("webinar_rsvps").insert({ webinar_id: webinar.id, user_id: user.id });
    setSaving(false);
    if (!error || error.code === "23505") {
      setSaved(true);
      trackEvent("hba_webinar_card_saved", { placement });
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(`${DISMISS_KEY}:${user.id}`, String(Date.now())); } catch { /* ignore */ }
    trackEvent("hba_webinar_card_dismissed", { placement });
    setVisible(false);
  };

  return (
    <Card className={`p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="size-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <CalendarCheck className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">Meet the Hero Benefits Alliance</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            A free {recorded ? "" : "live "}session on extra savings for people who serve, and how it works with your Stack.
            We'll also cover whether it may apply to you or your family, even if you're not a first responder, teacher or nurse.
          </p>
          {whenLabel && upcoming && <p className="text-sm font-medium text-foreground mt-2">Live on {whenLabel}</p>}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            {recorded ? (
              <Button size="sm" asChild>
                <a href={webinar.video_url!} target="_blank" rel="noopener noreferrer"
                  onClick={() => trackEvent("hba_webinar_card_saved", { placement, action: "watch" })}>
                  Watch the session
                </a>
              </Button>
            ) : saved ? (
              <p className="text-sm font-medium text-primary">
                You're on the list.{whenLabel && upcoming ? "" : " We'll let you know when it's scheduled."}
              </p>
            ) : (
              <Button size="sm" disabled={saving} onClick={save}>
                {whenLabel && upcoming ? "Save my spot" : "Notify me when it's scheduled"}
              </Button>
            )}
            {!saved && (
              <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground" onClick={dismiss}>
                Not now
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Hero rewards are provided by the Hero Benefits Alliance and its participating professionals, not by Home Approach. Home Approach receives nothing from HBA. You're never required to use any provider.
          </p>
        </div>
      </div>
    </Card>
  );
}
